/**
 * "What changed" — the author's review surface over the content repo's history.
 *
 * Three panes, narrowing left to right: when in the book's life, what changed
 * then, and what the change actually reads like. Selecting one commit shows
 * that commit's own work; shift-clicking a second spans the range, the way a
 * git client does.
 */

import { Close } from '@mui/icons-material'
import { Box, Button, Dialog, IconButton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { CURRENT_REVISION } from '@shared/contentHistory'
import type { ChangeMapResponse, HistoryTimelineEntry, SectionDiffResponse } from '@shared/contentHistory'
import type { SegmentChange } from '@shared/diff/types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import ChangeIndex from './ChangeIndex'
import HistoryTimeline from './HistoryTimeline'
import SectionDiffView, { DiffViewMode } from './SectionDiffView'
import { apiService } from '../../../../services/api'
import { logger } from '../../../../utils/logger'

interface HistoryDialogProps {
  open: boolean
  onClose: () => void
  bookId: string
  direction: 'ltr' | 'rtl'
  bookFontFamily?: string
}

const PAGE_SIZE = 30

export default function HistoryDialog({ open, onClose, bookId, direction, bookFontFamily }: HistoryDialogProps) {
  const [entries, setEntries] = useState<HistoryTimelineEntry[]>([])
  const [timelineLoading, setTimelineLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  /** Commits consumed so far — not rows held, since empty commits are dropped. */
  const [nextSkip, setNextSkip] = useState(0)

  const [selectedTo, setSelectedTo] = useState<string | null>(null)
  const [selectedFrom, setSelectedFrom] = useState<string | null>(null)

  const [changes, setChanges] = useState<ChangeMapResponse | null>(null)
  const [changesLoading, setChangesLoading] = useState(false)

  /** The change-map row the reader is pointed at — scrolls the diff pane, never replaces it. */
  const [selectedEntry, setSelectedEntry] = useState<SegmentChange | null>(null)
  const [diffs, setDiffs] = useState<SectionDiffResponse[]>([])
  const [diffsLoading, setDiffsLoading] = useState(false)
  const [diffsMeta, setDiffsMeta] = useState<{ truncated: boolean; totalSections: number }>({
    truncated: false,
    totalSections: 0,
  })

  const [viewMode, setViewMode] = useState<DiffViewMode>('inline')
  /**
   * What the selection is measured against.
   *
   * `commit` answers "what did this change"; `now` answers "how far has the book
   * moved since". Different questions, same selection.
   */
  const [compareTo, setCompareTo] = useState<'commit' | 'now'>('commit')

  const isRtl = direction === 'rtl'

  /** Identifies the newest range request, so a superseded one cannot clear its flags. */
  const rangeRequestRef = useRef(0)

  const loadTimeline = useCallback(
    async (skip: number) => {
      setTimelineLoading(true)
      try {
        const result = await apiService.getHistoryTimeline(bookId, { limit: PAGE_SIZE, skip })
        setHasMore(result.hasMore)
        setNextSkip(result.nextSkip)
        setEntries((current) => (skip === 0 ? result.entries : [...current, ...result.entries]))
        if (skip === 0 && result.entries.length > 0) {
          setSelectedTo((current) => current ?? result.entries[0].sha)
        }
      } catch (error) {
        logger.error('Failed to load content history timeline', error)
        toast.error('Could not read the content history')
      } finally {
        setTimelineLoading(false)
      }
    },
    [bookId],
  )

  useEffect(() => {
    if (!open) {
      return
    }
    void loadTimeline(0)
  }, [open, bookId, loadTimeline])

  // The change map follows the selection.
  useEffect(() => {
    if (!open || !selectedTo) {
      return
    }

    let cancelled = false
    const requestId = ++rangeRequestRef.current
    setChangesLoading(true)
    setDiffsLoading(true)
    setSelectedEntry(null)

    // Against a commit: its parent up to it, i.e. what it changed. Against now:
    // from the oldest selected commit up to the live manuscript.
    const rangeTo = compareTo === 'now' ? CURRENT_REVISION : selectedTo
    const rangeFrom = compareTo === 'now' ? (selectedFrom ?? selectedTo) : (selectedFrom ?? undefined)

    const load = async () => {
      try {
        // Both halves of the range in parallel: the map is the navigator, the
        // diffs are the document, and neither is useful without the other.
        const [map, allDiffs] = await Promise.all([
          apiService.getHistoryChanges(bookId, rangeTo, rangeFrom),
          apiService.getHistoryAllDiffs(bookId, rangeTo, rangeFrom),
        ])
        if (cancelled) {
          return
        }
        setChanges(map)
        setDiffs(allDiffs.sections)
        setDiffsMeta({ truncated: allDiffs.truncated, totalSections: allDiffs.totalSections })
      } catch (error) {
        logger.error('Failed to load history range', error)
        if (!cancelled) {
          setChanges(null)
          setDiffs([])
          setDiffsMeta({ truncated: false, totalSections: 0 })
        }
      } finally {
        // Only the newest request owns the flags. Clearing them unconditionally
        // would drop the spinner while a newer range is still loading; guarding
        // on `cancelled` instead would strand it forever when a run ends with
        // nothing to replace it.
        if (rangeRequestRef.current === requestId) {
          setChangesLoading(false)
          setDiffsLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [open, bookId, selectedTo, selectedFrom, compareTo])

  const handleSelectCommit = useCallback(
    (sha: string, extend: boolean) => {
      if (!extend) {
        setSelectedTo(sha)
        setSelectedFrom(null)
        return
      }

      // Shift-click spans from the older of the two commits to the newer.
      const anchorIndex = entries.findIndex((entry) => entry.sha === (selectedTo ?? sha))
      const clickedIndex = entries.findIndex((entry) => entry.sha === sha)
      if (anchorIndex === -1 || clickedIndex === -1) {
        setSelectedTo(sha)
        setSelectedFrom(null)
        return
      }
      const newer = entries[Math.min(anchorIndex, clickedIndex)]
      const older = entries[Math.max(anchorIndex, clickedIndex)]
      setSelectedTo(newer.sha)
      setSelectedFrom(older.sha)
    },
    [entries, selectedTo],
  )

  /** Presets. Each resolves to a `from` commit; `to` is always the newest one. */
  const applyPreset = useCallback(
    (preset: 'latest' | 'today' | 'week') => {
      if (entries.length === 0) {
        return
      }
      const head = entries[0]

      if (preset === 'latest') {
        // The newest commit on its own — a null `from` means "against its parent".
        setSelectedTo(head.sha)
        setSelectedFrom(null)
        return
      }

      const cutoff = new Date()
      if (preset === 'today') {
        cutoff.setHours(0, 0, 0, 0)
      } else {
        cutoff.setDate(cutoff.getDate() - 7)
      }
      const base = entries.find((entry) => new Date(entry.date) < cutoff)
      setSelectedTo(head.sha)
      setSelectedFrom(base ? base.sha : null)
    },
    [entries],
  )

  const rangeLabel = useMemo(() => {
    if (!changes) {
      return ''
    }
    const total = changes.totals
    const start = changes.from ? new Date(changes.from.date).toLocaleDateString() : null
    const end = changes.to ? new Date(changes.to.date).toLocaleDateString() : 'now'
    const scope = start ? `${start} → ${end}` : compareTo === 'now' ? `up to ${end}` : 'one commit'
    return `${scope} · ${total.sectionsChanged} section${total.sectionsChanged === 1 ? '' : 's'} · +${total.wordsAdded} / −${total.wordsRemoved} words`
  }, [changes, compareTo])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: '92vh', bgcolor: 'var(--bg-surface)' } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: '1px solid var(--border-subtle)',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>What changed</Typography>

        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
          <Button size="small" sx={{ fontSize: '0.7rem' }} onClick={() => applyPreset('latest')}>
            Latest
          </Button>
          <Button size="small" sx={{ fontSize: '0.7rem' }} onClick={() => applyPreset('today')}>
            Today
          </Button>
          <Button size="small" sx={{ fontSize: '0.7rem' }} onClick={() => applyPreset('week')}>
            7 days
          </Button>
        </Box>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={compareTo}
          onChange={(_event, next) => next && setCompareTo(next)}
          sx={{ ml: 1, '& .MuiToggleButton-root': { fontSize: '0.68rem', py: 0.25, px: 1, textTransform: 'none' } }}
        >
          <ToggleButton value="commit">What it changed</ToggleButton>
          <ToggleButton value="now">Against now</ToggleButton>
        </ToggleButtonGroup>

        <Typography sx={{ flex: 1, fontSize: '0.72rem', color: 'var(--text-tertiary)', textAlign: 'end' }}>
          {rangeLabel}
        </Typography>

        <IconButton size="small" onClick={onClose} aria-label="Close">
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
        <Box
          sx={{
            width: 260,
            flexShrink: 0,
            borderInlineEnd: '1px solid var(--border-subtle)',
            minHeight: 0,
          }}
        >
          <HistoryTimeline
            entries={entries}
            loading={timelineLoading}
            hasMore={hasMore}
            onLoadMore={() => void loadTimeline(nextSkip)}
            selectedTo={selectedTo}
            selectedFrom={selectedFrom}
            onSelect={handleSelectCommit}
            direction={direction}
          />
        </Box>

        <Box
          sx={{
            width: 280,
            flexShrink: 0,
            overflowY: 'auto',
            borderInlineEnd: '1px solid var(--border-subtle)',
            minHeight: 0,
          }}
        >
          <ChangeIndex
            changes={changes}
            loading={changesLoading}
            selectedKey={selectedEntry?.key ?? null}
            onSelect={setSelectedEntry}
            direction={direction}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          <SectionDiffView
            sections={diffs}
            loading={diffsLoading}
            truncated={diffsMeta.truncated}
            totalSections={diffsMeta.totalSections}
            direction={direction}
            fontFamily={bookFontFamily}
            mode={viewMode}
            onModeChange={setViewMode}
            focusKey={selectedEntry?.key ?? null}
          />
        </Box>
      </Box>
    </Dialog>
  )
}
